import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft } from 'lucide-react';
import { supabase } from '../../supabase/supabaseClient';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('checking'); // checking | ready | success | error
  const [error, setError] = useState('');

  useEffect(() => {
    async function checkSession() {
      try {
        const { data } = await supabase.auth.getSession();
        const session = data?.session;
        if (!session || !session.user) {
          setStatus('error');
          setError('Your reset link is invalid or has expired. Please request a new one.');
        } else {
          setStatus('ready');
        }
      } catch (err) {
        console.error('[ResetPassword] session check error:', err);
        setStatus('error');
        setError('Unable to validate reset link. Please request a new one.');
      }
    }
    checkSession();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!password || !confirmPassword) {
      setError('Please enter and confirm your new password.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        throw updateError;
      }
      setStatus('success');
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1500);
    } catch (err) {
      console.error('[ResetPassword] update error:', err);
      setError(err.message || 'Failed to update password. Please try again.');
    }
  };

  const disabled = status !== 'ready';

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="p-6">
        <Link to="/login" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-medium">
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 pb-20">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Choose a new password</h1>
            <p className="text-slate-400 text-sm">
              {status === 'checking' && 'Verifying your reset link…'}
              {status === 'ready' && 'Enter a strong password to secure your account.'}
              {status === 'success' && 'Password updated successfully. Redirecting to login…'}
              {status === 'error' && 'We could not validate your reset link.'}
            </p>
          </div>

          <div className="bg-surface p-8 rounded-2xl border border-white/10 shadow-2xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="password" className="text-sm font-medium text-slate-400 block mb-2">New password</label>
                <div className="relative">
                  <Lock className="w-5 h-5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="w-full bg-black/20 border border-white/10 rounded-lg py-3 pl-10 pr-4 text-white focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none transition-all"
                    disabled={disabled}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="text-sm font-medium text-slate-400 block mb-2">Confirm password</label>
                <div className="relative">
                  <Lock className="w-5 h-5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat your new password"
                    className="w-full bg-black/20 border border-white/10 rounded-lg py-3 pl-10 pr-4 text-white focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none transition-all"
                    disabled={disabled}
                  />
                </div>
              </div>

              {error && (
                <div className="text-red-400 text-xs mb-2 text-center bg-red-500/10 p-2 rounded border border-red-500/20">{error}</div>
              )}

              <button
                type="submit"
                disabled={disabled}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary text-white font-bold rounded-lg transition-all duration-300 hover:shadow-glow-blue hover:bg-primary-hover transform hover:-translate-y-0.5 disabled:opacity-60"
              >
                Set new password
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
